import { createBrowserRouter, Navigate } from 'react-router';
import { AdminLayout, type RouteHandle } from '@/components/layout/AdminLayout';
import { MobileLayout } from '@/components/layout/MobileLayout';
import { MobileEntry, RedirectIfAuthenticated, RequireAuth, RequirePermission } from './guards';
import { NotFoundPage } from './NotFoundPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';

/**
 * Feature screens are code-split per route (React Router's `lazy`), so the
 * initial load carries only the shell + login. The charting library, for
 * instance, is fetched the first time someone opens the dashboard.
 */

/** Typed helper so every admin route declares its topbar title. */
const handle = (title: string, subtitle?: string): RouteHandle => ({ title, subtitle });

/**
 * As above, but the topbar also shows the date filter.
 *
 * Reserved for screens whose figures are genuinely date-scoped. Reports keeps
 * its own From/To fields inside the filter card — two controls driving one query
 * from opposite ends of the page is a bug generator, so it is not listed here.
 */
const dateScoped = (title: string, subtitle?: string): RouteHandle => ({
  title,
  subtitle,
  dateFilter: true,
});

export const router = createBrowserRouter([
  {
    element: <RedirectIfAuthenticated />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
    ],
  },

  {
    element: <RequireAuth />,
    children: [
      /* ------------------------------ admin panel ------------------------------ */
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          {
            path: '/dashboard',
            handle: dateScoped('Dashboard', "Welcome back! Here's what's happening today."),
            lazy: async () => ({
              Component: (await import('@/features/dashboard/DashboardPage')).DashboardPage,
            }),
          },
          {
            // Viewing generated batches (QR_VIEW) and generating new ones
            // (QR_GENERATE) are separate grants; the page itself hides the
            // generate form and print/download actions a viewer lacks.
            element: <RequirePermission permissions={['QR_VIEW', 'QR_GENERATE']} />,
            children: [
              {
                path: '/qr-generation',
                handle: handle('QR Code Generation', 'Create and print internal carton labels'),
                lazy: async () => ({
                  Component: (await import('@/features/qr/QrGenerationPage')).QrGenerationPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['USER_VIEW']} />,
            children: [
              {
                path: '/users',
                handle: handle('Users', 'Manage accounts and roles'),
                lazy: async () => ({
                  Component: (await import('@/features/users/UsersPage')).UsersPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['ROLE_VIEW']} />,
            children: [
              {
                path: '/roles',
                handle: handle('Roles & Permissions', 'Decide what each role can see and do'),
                lazy: async () => ({
                  Component: (await import('@/features/roles/RolesPage')).RolesPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['CARTON_RECEIVE']} />,
            children: [
              {
                path: '/receive',
                handle: handle('Receive Cartons', 'Scan internal QR code to receive cartons'),
                lazy: async () => ({
                  Component: (await import('@/features/receive/ReceiveCartonsPage'))
                    .ReceiveCartonsPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['RECEIVED_LOT_VIEW']} />,
            children: [
              {
                path: '/received-lots',
                handle: handle('Received Lots', 'Lots generated through Receive Cartons, by lot number'),
                lazy: async () => ({
                  Component: (await import('@/features/receive/ReceivedLotsPage'))
                    .ReceivedLotsPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['DISPATCHED_LOT_VIEW']} />,
            children: [
              {
                path: '/dispatched-lots',
                handle: handle('Dispatched Lots', 'Every lot with at least one carton dispatched, by lot number'),
                lazy: async () => ({
                  Component: (await import('@/features/dispatch/DispatchedLotsPage')).DispatchedLotsPage,
                }),
              },
            ],
          },
          {
            // Entering vs editing is decided per row (COMMERCIAL_INFO_CREATE /
            // _EDIT) inside the page; viewing the list only needs VIEW.
            element: <RequirePermission permissions={['COMMERCIAL_INFO_VIEW']} />,
            children: [
              {
                path: '/commercial-info',
                handle: handle('Commercial Info', 'Invoice, destination and shipping details for fully-dispatched lots'),
                lazy: async () => ({
                  Component: (await import('@/features/commercial-info/CommercialInfoPage'))
                    .CommercialInfoPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['CARTON_DISPATCH']} />,
            children: [
              {
                path: '/dispatch',
                handle: handle('Dispatch Cartons', 'Scan internal QR code to dispatch cartons for shipment'),
                lazy: async () => ({
                  Component: (await import('@/features/dispatch/DispatchCartonsPage'))
                    .DispatchCartonsPage,
                }),
              },
            ],
          },
          /*
           * Business Setup is 5 independent screens, not one feature: a role
           * can hold BUYER_VIEW without STYLE_VIEW and see only that submenu,
           * and holding a *_VIEW without the matching *_MANAGE opens the page
           * read-only (the page itself hides Create/Edit/Delete — see
           * BuyerPage etc.). Gating all 5 on one permission was the bug.
           */
          {
            element: <RequirePermission permissions={['BUYER_VIEW', 'BUYER_MANAGE']} />,
            children: [
              {
                path: '/business-setup/buyers',
                handle: handle('Buyer', 'Create and manage buyers'),
                lazy: async () => ({
                  Component: (await import('@/features/business-setup/BuyerPage')).BuyerPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['PURCHASE_ORDER_VIEW', 'PURCHASE_ORDER_MANAGE']} />,
            children: [
              {
                path: '/business-setup/purchase-orders',
                handle: handle('Purchase Order', 'Create and manage purchase orders'),
                lazy: async () => ({
                  Component: (await import('@/features/business-setup/PurchaseOrderPage'))
                    .PurchaseOrderPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['STYLE_VIEW', 'STYLE_MANAGE']} />,
            children: [
              {
                path: '/business-setup/styles',
                handle: handle('Style', 'Create and manage styles'),
                lazy: async () => ({
                  Component: (await import('@/features/business-setup/StylePage')).StylePage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['LOT_NUMBER_VIEW', 'LOT_NUMBER_MANAGE']} />,
            children: [
              {
                path: '/business-setup/lot-numbers',
                handle: handle('Lot Number', 'Create and manage lot numbers'),
                lazy: async () => ({
                  Component: (await import('@/features/business-setup/LotNumberPage'))
                    .LotNumberPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['SIZE_VIEW', 'SIZE_MANAGE']} />,
            children: [
              {
                path: '/business-setup/sizes',
                handle: handle('Size', 'Create and manage sizes'),
                lazy: async () => ({
                  Component: (await import('@/features/business-setup/SizePage')).SizePage,
                }),
              },
            ],
          },
          {
            // No SHIPMENT_VIEW exists — reading a shipment is already covered by
            // CARTON_VIEW/CARTON_DISPATCH wherever a carton screen shows one (see
            // Permissions.java). This page is for whoever plans them.
            element: <RequirePermission permissions={['SHIPMENT_MANAGE']} />,
            children: [
              {
                path: '/business-setup/shipments',
                handle: handle('Shipments', 'Plan shipments so received cartons become dispatch-eligible'),
                lazy: async () => ({
                  Component: (await import('@/features/business-setup/ShipmentPage')).ShipmentPage,
                }),
              },
            ],
          },
          {
            element: <RequirePermission permissions={['REPORT_VIEW']} />,
            children: [
              {
                path: '/reports',
                handle: handle('Reports', 'Filter and export carton movement'),
                lazy: async () => ({
                  Component: (await import('@/features/reports/ReportsPage')).ReportsPage,
                }),
              },
            ],
          },
          {
            path: '/settings',
            handle: handle('Settings', 'Profile and system configuration'),
            lazy: async () => ({
              Component: (await import('@/features/settings/SettingsPage')).SettingsPage,
            }),
          },
          /*
           * Platform operators only — the seeded PLATFORM_ADMIN role is the one
           * account holding TENANT_VIEW, so this is unreachable for every
           * ordinary workspace. See PlatformAdminSeeder (backend).
           */
          {
            element: <RequirePermission permissions={['TENANT_VIEW']} />,
            children: [
              {
                path: '/admin/workspaces',
                handle: handle('Workspaces', 'Every company registered on the platform'),
                lazy: async () => ({
                  Component: (await import('@/features/platform/WorkspacesPage')).WorkspacesPage,
                }),
              },
            ],
          },
        ],
      },

      /* ---------------------------- receiver mobile ---------------------------- */
      { path: '/m', element: <MobileEntry /> },
      {
        element: <RequirePermission permissions={['CARTON_RECEIVE']} />,
        children: [
          {
            path: '/m/receive',
            element: <MobileLayout theme="receive" />,
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import('@/features/mobile/MobileDashboards'))
                    .MobileReceiveDashboard,
                }),
              },
              {
                path: 'scan',
                lazy: async () => ({
                  Component: (await import('@/features/mobile/ReceiveScanFlow'))
                    .MobileReceiveScanFlow,
                }),
              },
            ],
          },
        ],
      },

      /* --------------------------- dispatcher mobile -------------------------- */
      {
        element: <RequirePermission permissions={['CARTON_DISPATCH']} />,
        children: [
          {
            path: '/m/dispatch',
            element: <MobileLayout theme="dispatch" />,
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import('@/features/mobile/MobileDashboards'))
                    .MobileDispatchDashboard,
                }),
              },
              {
                path: 'scan',
                lazy: async () => ({
                  Component: (await import('@/features/mobile/DispatchScanFlow'))
                    .MobileDispatchScanFlow,
                }),
              },
            ],
          },
        ],
      },

      /* --------------------- shared mobile screens (both) --------------------- */
      {
        path: '/m',
        element: <MobileLayout />,
        children: [
          {
            path: 'history',
            lazy: async () => ({
              Component: (await import('@/features/mobile/ScanHistoryScreen'))
                .MobileScanHistoryScreen,
            }),
          },
          {
            path: 'profile',
            lazy: async () => ({
              Component: (await import('@/features/mobile/ProfileScreen')).MobileProfileScreen,
            }),
          },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);
